import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

import useAuthStore from './store/authStore';
import useSettingsStore from './store/settingsStore';
import i18n from './i18n';

// Layouts
import AdminLayout from './components/Layout/AdminLayout';
import StudentLayout from './components/Layout/StudentLayout';
import ProctorLayout from './components/Layout/ProctorLayout';
import SuperAdminLayout from './components/Layout/SuperAdminLayout';

// Guards
import ProtectedRoute from './components/ProtectedRoute';

// Auth
import Login from './pages/auth/Login';

// Admin pages
import AdminDashboard from './pages/admin/Dashboard';
import UserManagement from './pages/admin/UserManagement';
import ExamManagement from './pages/admin/ExamManagement';
import ExamDetail from './pages/admin/ExamDetail';
import AdminSettings from './pages/admin/Settings';
import ExamLogs from './pages/admin/ExamLogs';
import ThirdPartyManagement from './pages/admin/ThirdPartyManagement';

// Student pages
import StudentDashboard from './pages/student/StudentDashboard';
import ExamRoom from './pages/student/ExamRoom';
import MockTestRoom from './pages/student/MockTestRoom';

// Proctor pages
import ProctorDashboard from './pages/proctor/ProctorDashboard';
import MessagingPanel from './pages/proctor/MessagingPanel';

// Super Admin pages
import SuperAdminDashboard from './pages/superadmin/SuperAdminDashboard';
import ActivityTimeline from './pages/superadmin/ActivityTimeline';
import AllLogs from './pages/superadmin/AllLogs';
import DatabaseExplorer from './pages/superadmin/DatabaseExplorer';
import QueryChat from './pages/superadmin/QueryChat';
import SuperAdminExams from './pages/superadmin/SuperAdminExams';

// Role-based root redirect
const RootRedirect = () => {
  const { isAuthenticated, user } = useAuthStore();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user?.role === 'SUPER_ADMIN') return <Navigate to="/super-admin/dashboard" replace />;
  if (user?.role === 'ADMIN') return <Navigate to="/admin/dashboard" replace />;
  if (user?.role === 'STUDENT') return <Navigate to="/student/dashboard" replace />;
  if (user?.role === 'PROCTOR') return <Navigate to="/proctor/dashboard" replace />;
  return <Navigate to="/login" replace />;
};

const App = () => {
  const { fetchSettings, primaryColor, secondaryColor, portalName, logoUrl, defaultLanguage, loaded } = useSettingsStore();

  useEffect(() => {
    fetchSettings();
  }, []);

  // Apply the admin-configured default language to visitors who haven't
  // explicitly picked their own yet (LanguageSwitcher persists an explicit
  // choice to this same localStorage key via i18next-browser-languagedetector).
  // Applies across admin/proctor/student chrome alike since i18n is a single
  // global instance shared by the whole app.
  useEffect(() => {
    if (!loaded || !defaultLanguage) return;
    let hasExplicitChoice = false;
    try { hasExplicitChoice = !!localStorage.getItem('examPortalLanguage'); } catch (e) { /* ignore */ }
    if (!hasExplicitChoice) i18n.changeLanguage(defaultLanguage);
  }, [loaded, defaultLanguage]);

  // Update browser tab title whenever portalName changes
  useEffect(() => {
    document.title = portalName || 'Primosa Exams';
  }, [portalName]);

  // White-label the browser tab icon too — without this, every institution's
  // deployment showed Primosa's own logo regardless of their configured
  // branding, which defeats the point of white-labeling.
  useEffect(() => {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = logoUrl || '/assets/logo.png';
  }, [logoUrl]);

  return (
    <React.Fragment>
      <style>
        {`
          :root {
            --theme-primary: ${primaryColor || '#4f46e5'};
            --theme-secondary: ${secondaryColor || '#0f172a'};
          }
        `}
      </style>
      <BrowserRouter>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#1e293b',
            color: '#f1f5f9',
            fontSize: '14px',
            borderRadius: '10px',
            padding: '12px 16px'
          },
          success: {
            iconTheme: { primary: '#22c55e', secondary: '#f1f5f9' }
          },
          error: {
            iconTheme: { primary: '#ef4444', secondary: '#f1f5f9' }
          }
        }}
      />

      <Routes>
        {/* Root */}
        <Route path="/" element={<RootRedirect />} />

        {/* Auth */}
        <Route path="/login" element={<Login />} />

        {/* Admin Routes */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="users" element={<UserManagement />} />
          <Route path="exams" element={<ExamManagement />} />
          <Route path="exams/:id" element={<ExamDetail />} />
          <Route path="logs" element={<ExamLogs />} />
          <Route path="settings" element={<AdminSettings />} />
          <Route path="third-party" element={<ThirdPartyManagement />} />
        </Route>

        {/* Student Routes */}
        <Route
          path="/student"
          element={
            <ProtectedRoute allowedRoles={['STUDENT']}>
              <StudentLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/student/dashboard" replace />} />
          <Route path="dashboard" element={<StudentDashboard />} />
        </Route>

        {/* Exam Room - no layout wrapper (fullscreen) */}
        <Route
          path="/student/exam/:examId"
          element={
            <ProtectedRoute allowedRoles={['STUDENT']}>
              <ExamRoom />
            </ProtectedRoute>
          }
        />

        {/* Mock Test Room */}
        <Route
          path="/student/mock/:examId"
          element={
            <ProtectedRoute allowedRoles={['STUDENT']}>
              <MockTestRoom />
            </ProtectedRoute>
          }
        />

        {/* Proctor Routes */}
        <Route
          path="/proctor"
          element={
            <ProtectedRoute allowedRoles={['PROCTOR']}>
              <ProctorLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/proctor/dashboard" replace />} />
          <Route path="dashboard" element={<ProctorDashboard />} />
          <Route path="messaging" element={<MessagingPanel />} />
        </Route>

        {/* Super Admin Routes */}
        <Route
          path="/super-admin"
          element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <SuperAdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/super-admin/dashboard" replace />} />
          <Route path="dashboard" element={<SuperAdminDashboard />} />
          <Route path="timeline" element={<ActivityTimeline />} />
          <Route path="logs" element={<AllLogs />} />
          <Route path="database" element={<DatabaseExplorer />} />
          <Route path="exams" element={<SuperAdminExams />} />
          <Route path="chat" element={<QueryChat />} />
          <Route path="third-party" element={<ThirdPartyManagement />} />
        </Route>

        {/* 404 catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
    </React.Fragment>
  );
};

export default App;
